/**
 * OpenDataModeler RUM (Real User Monitoring) SDK
 *
 * 브라우저에서 실제 사용자 성능 데이터를 수집하여 QPM 서버로 전송
 *
 * 수집 항목:
 * - API 호출 성능 (URL, 메서드, 상태코드, 응답시간)
 * - 페이지 로드 시간 (DNS, TCP, TTFB, DOM 로드)
 * - Core Web Vitals (LCP, FID, CLS, FCP, TTFB, INP)
 * - JavaScript 에러
 * - 커스텀 이벤트
 * - 컴포넌트 렌더링 성능
 *
 * 사용법:
 * --------------------------------------------------------
 * import { initRUM, enableAutoTracking } from './utils/rum';
 *
 * initRUM({
 *   endpoint: 'https://opendatamodeler.com:3006/api/client-metrics',
 *   appName: 'opendatamodeler',
 *   sampleRate: 1.0
 * });
 *
 * enableAutoTracking();  // fetch 자동 추적 활성화
 * --------------------------------------------------------
 */

import { createQPM } from '../lib/qpm-sdk/frontend';
import { QPM_ENABLED } from '../lib/qpm-sdk/enabled';

// ============================================
// 설정 및 상태
// ============================================

let config = {
    endpoint: '/api/client-metrics',
    appName: 'opendatamodeler',
    appVersion: '1.0.0',
    sampleRate: 1.0,
    flushInterval: 10000,
    maxBatchSize: 50,
    slowThreshold: 500,
    debug: false,
    enabled: true  // false로 설정하면 RUM 비활성화
};

let metricsBuffer = [];
let flushTimer = null;
let sessionId = null;
let originalFetch = null;
let qpmInstance = null;
let clsValue = 0;

// ============================================
// 초기화
// ============================================

/**
 * RUM SDK 초기화
 * @param {Object} options - 설정 옵션
 */
export function initRUM(options = {}) {
    if (sessionId) {
        debugLog('Already initialized');
        return;
    }

    config = { ...config, ...options };

    // QPM 기능 전체가 꺼져 있으면 어떤 설정으로도 켜지지 않는다 (lib/qpm-sdk/enabled.js)
    if (!QPM_ENABLED) {
        config.enabled = false;
        debugLog('QPM is disabled — RUM not initialized');
        return;
    }

    // RUM 비활성화 시 초기화 건너뛰기
    if (!config.enabled) {
        debugLog('RUM is disabled');
        return;
    }

    sessionId = generateSessionId();

    debugLog('Initializing with config:', config);

    // QPM SDK 초기화 (선택적)
    if (config.endpoint) {
        try {
            const qpmEndpoint = config.endpoint.replace('/api/client-metrics', '');
            if (qpmEndpoint && !qpmEndpoint.endsWith('/api/client-metrics')) {
                qpmInstance = createQPM({
                    serviceName: config.appName,
                    qpmServerUrl: qpmEndpoint,
                    sampleRate: config.sampleRate,
                    batchSize: config.maxBatchSize,
                    flushInterval: config.flushInterval,
                    slowThreshold: config.slowThreshold
                });
                qpmInstance.init().catch(err => {
                    debugLog('QPM SDK init warning:', err.message);
                });
            }
        } catch (error) {
            debugLog('QPM SDK init skipped:', error.message);
        }
    }

    if (typeof window !== 'undefined') {
        // 페이지 로드 성능 수집
        if (document.readyState === 'complete') {
            collectPageLoadMetrics();
        } else {
            window.addEventListener('load', collectPageLoadMetrics);
        }

        // 페이지 언로드 시 버퍼 플러시
        window.addEventListener('beforeunload', () => flushMetrics(true));

        // Web Vitals 수집 (PerformanceObserver 기반)
        collectWebVitals();

        // 주기적 플러시 시작
        startPeriodicFlush();
    }

    debugLog('Initialized successfully');
}

// ============================================
// 메트릭 수집 함수
// ============================================

/**
 * API 호출 성능 추적
 * @param {string} url - API URL
 * @param {string} method - HTTP 메서드
 * @param {number} statusCode - 응답 상태 코드
 * @param {number} duration - 응답 시간 (ms)
 * @param {Object} extra - 추가 데이터
 */
export function trackAPICall(url, method, statusCode, duration, extra = {}) {
    if (!shouldSample()) return;

    const metric = {
        type: 'api_call',
        timestamp: Date.now(),
        sessionId,
        data: {
            url: normalizeUrl(url),
            method: method.toUpperCase(),
            statusCode,
            duration: Math.round(duration),
            success: statusCode >= 200 && statusCode < 400,
            ...extra
        }
    };

    addToBuffer(metric);
    debugLog('API Call:', metric.data.method, metric.data.url, metric.data.statusCode, `${metric.data.duration}ms`);
}

/**
 * 페이지 로드 메트릭 수집
 */
function collectPageLoadMetrics() {
    // 브라우저가 타이밍 데이터를 완성할 때까지 약간 대기
    setTimeout(() => {
        try {
            const navigation = performance.getEntriesByType?.('navigation')?.[0];
            const timing = window.performance.timing;

            if (navigation || timing) {
                const metric = {
                    type: 'page_load',
                    timestamp: Date.now(),
                    sessionId,
                    data: {
                        url: window.location.pathname,
                        // Navigation Timing
                        dns: navigation?.domainLookupEnd - navigation?.domainLookupStart
                            || timing?.domainLookupEnd - timing?.domainLookupStart || 0,
                        tcp: navigation?.connectEnd - navigation?.connectStart
                            || timing?.connectEnd - timing?.connectStart || 0,
                        ssl: navigation?.secureConnectionStart > 0
                            ? navigation.connectEnd - navigation.secureConnectionStart
                            : (timing?.secureConnectionStart > 0
                                ? timing.connectEnd - timing.secureConnectionStart : 0),
                        ttfb: navigation?.responseStart - navigation?.requestStart
                            || timing?.responseStart - timing?.requestStart || 0,
                        download: navigation?.responseEnd - navigation?.responseStart
                            || timing?.responseEnd - timing?.responseStart || 0,
                        domInteractive: navigation?.domInteractive
                            || (timing?.domInteractive - timing?.navigationStart) || 0,
                        domComplete: navigation?.domComplete
                            || (timing?.domComplete - timing?.navigationStart) || 0,
                        loadComplete: navigation?.loadEventEnd
                            || (timing?.loadEventEnd - timing?.navigationStart) || 0,
                        // First Paint
                        firstPaint: getFirstPaint(),
                        firstContentfulPaint: getFirstContentfulPaint(),
                        // Resource info
                        resourceCount: performance.getEntriesByType?.('resource')?.length || 0,
                        transferSize: navigation?.transferSize || 0,
                        encodedBodySize: navigation?.encodedBodySize || 0,
                        decodedBodySize: navigation?.decodedBodySize || 0,
                        navigationType: navigation?.type || 'navigate'
                    }
                };

                addToBuffer(metric);
                debugLog('Page Load:', metric.data);
            }
        } catch (error) {
            debugLog('Page load metrics collection failed:', error);
        }
    }, 100);
}

/**
 * Web Vitals (Core Web Vitals) 수집 - PerformanceObserver 기반
 */
function collectWebVitals() {
    if (typeof PerformanceObserver === 'undefined') {
        debugLog('PerformanceObserver not supported');
        return;
    }

    try {
        // LCP (Largest Contentful Paint)
        const lcpObserver = new PerformanceObserver((list) => {
            const entries = list.getEntries();
            const lastEntry = entries[entries.length - 1];
            const value = Math.round(lastEntry.startTime);

            addToBuffer({
                type: 'web_vital',
                timestamp: Date.now(),
                sessionId,
                data: {
                    name: 'LCP',
                    value,
                    rating: value < 2500 ? 'good' : value < 4000 ? 'needs-improvement' : 'poor'
                }
            });
            debugLog('LCP:', value, 'ms');
        });
        lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });

        // FID (First Input Delay)
        const fidObserver = new PerformanceObserver((list) => {
            const entries = list.getEntries();
            entries.forEach(entry => {
                const value = Math.round(entry.processingStart - entry.startTime);

                addToBuffer({
                    type: 'web_vital',
                    timestamp: Date.now(),
                    sessionId,
                    data: {
                        name: 'FID',
                        value,
                        rating: value < 100 ? 'good' : value < 300 ? 'needs-improvement' : 'poor'
                    }
                });
                debugLog('FID:', value, 'ms');
            });
        });
        fidObserver.observe({ type: 'first-input', buffered: true });

        // CLS (Cumulative Layout Shift)
        const clsObserver = new PerformanceObserver((list) => {
            list.getEntries().forEach(entry => {
                if (!entry.hadRecentInput) {
                    clsValue += entry.value;
                }
            });
        });
        clsObserver.observe({ type: 'layout-shift', buffered: true });

        // CLS 값 주기적 보고 (30초마다)
        setInterval(() => {
            if (clsValue > 0) {
                const value = Math.round(clsValue * 1000) / 1000;
                addToBuffer({
                    type: 'web_vital',
                    timestamp: Date.now(),
                    sessionId,
                    data: {
                        name: 'CLS',
                        value,
                        rating: value < 0.1 ? 'good' : value < 0.25 ? 'needs-improvement' : 'poor'
                    }
                });
                debugLog('CLS:', value);
            }
        }, 30000);

        // FCP, TTFB (web-vitals 라이브러리 사용 시도)
        import('web-vitals').then(({ onFCP, onTTFB, onINP }) => {
            const handleVital = (metric) => {
                addToBuffer({
                    type: 'web_vital',
                    timestamp: Date.now(),
                    sessionId,
                    data: {
                        name: metric.name,
                        value: Math.round(metric.value),
                        rating: metric.rating,
                        delta: metric.delta,
                        navigationType: metric.navigationType
                    }
                });
                debugLog(metric.name + ':', metric.value, metric.rating);
            };

            onFCP(handleVital);
            onTTFB(handleVital);
            if (onINP) onINP(handleVital);
        }).catch(() => {
            debugLog('web-vitals library not available, using PerformanceObserver only');
        });

    } catch (e) {
        debugLog('Web Vitals collection error:', e);
    }
}

/**
 * 사용자 정의 이벤트 추적
 * @param {string} eventName - 이벤트 이름
 * @param {Object} data - 이벤트 데이터
 */
export function trackEvent(eventName, data = {}) {
    if (!shouldSample()) return;

    addToBuffer({
        type: 'custom_event',
        timestamp: Date.now(),
        sessionId,
        data: {
            name: eventName,
            ...data
        }
    });
    debugLog('Event:', eventName, data);
}

/**
 * 에러 추적
 * @param {Error} error - 에러 객체
 * @param {Object} context - 추가 컨텍스트
 */
export function trackError(error, context = {}) {
    // ResizeObserver 에러는 무시
    if (error?.message?.includes('ResizeObserver')) {
        return;
    }

    addToBuffer({
        type: 'error',
        timestamp: Date.now(),
        sessionId,
        data: {
            message: error?.message || String(error),
            stack: error?.stack?.substring(0, 1000),
            name: error?.name || 'Error',
            url: window.location.href,
            ...context
        }
    });
    debugLog('Error:', error?.message);
}

/**
 * 커스텀 타이밍 측정
 * @param {string} name - 타이밍 이름
 * @param {number} duration - 소요 시간 (ms)
 */
export function trackTiming(name, duration) {
    trackEvent('timing', { name, duration: Math.round(duration) });
}

// ============================================
// Fetch Wrapper (자동 API 추적)
// ============================================

/**
 * fetch를 래핑하여 자동으로 API 호출 추적
 * @returns {Function} 래핑된 fetch 함수
 */
export function createTrackedFetch() {
    const baseFetch = originalFetch || window.fetch;

    return async function trackedFetch(input, init = {}) {
        const startTime = performance.now();
        const url = typeof input === 'string' ? input : input.url;
        const method = init.method || 'GET';

        // metrics 엔드포인트는 추적 제외
        if (url.includes('/metrics') || url.includes('/api/client-metrics')) {
            return baseFetch(input, init);
        }

        try {
            const response = await baseFetch(input, init);
            const duration = performance.now() - startTime;

            trackAPICall(url, method, response.status, duration, {
                type: 'fetch'
            });

            return response;
        } catch (error) {
            const duration = performance.now() - startTime;

            trackAPICall(url, method, 0, duration, {
                type: 'fetch',
                error: error.message
            });

            throw error;
        }
    };
}

/**
 * 전역 fetch를 추적 버전으로 교체
 */
export function enableAutoTracking() {
    if (typeof window === 'undefined' || originalFetch) {
        debugLog('Auto tracking already enabled or not in browser');
        return;
    }

    originalFetch = window.fetch;
    window.fetch = createTrackedFetch();

    // 전역 에러 핸들러
    window.addEventListener('error', (event) => {
        trackError(event.error || new Error(event.message), {
            filename: event.filename,
            lineno: event.lineno,
            colno: event.colno,
            type: 'error'
        });
    });

    // Promise rejection 핸들러
    window.addEventListener('unhandledrejection', (event) => {
        trackError(event.reason, {
            type: 'unhandledrejection'
        });
    });

    debugLog('Auto tracking enabled');
}

/**
 * fetch 자동 추적 비활성화
 */
export function disableAutoTracking() {
    if (originalFetch) {
        window.fetch = originalFetch;
        originalFetch = null;
        debugLog('Auto tracking disabled');
    }
}

// ============================================
// React Hook
// ============================================

/**
 * React Hook for tracking component render performance
 * @param {string} componentName - 컴포넌트 이름
 */
export function useRenderTracking(componentName) {
    if (typeof window === 'undefined' || !sessionId) return;

    const startTime = performance.now();

    // useEffect equivalent for tracking render complete
    setTimeout(() => {
        const duration = performance.now() - startTime;
        if (duration > 16) {  // 16ms 이상만 추적 (60fps 기준)
            addToBuffer({
                type: 'render',
                timestamp: Date.now(),
                sessionId,
                data: {
                    component: componentName,
                    duration: Math.round(duration)
                }
            });
            debugLog('Render:', componentName, `${Math.round(duration)}ms`);
        }
    }, 0);
}

// ============================================
// 버퍼 관리 및 전송
// ============================================

function addToBuffer(metric) {
    metric.appName = config.appName;
    metric.appVersion = config.appVersion;
    metric.userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '';
    metric.pageUrl = typeof window !== 'undefined' ? window.location.href : '';

    metricsBuffer.push(metric);

    // 버퍼가 가득 차면 즉시 플러시
    if (metricsBuffer.length >= config.maxBatchSize) {
        flushMetrics();
    }
}

function startPeriodicFlush() {
    if (flushTimer) clearInterval(flushTimer);
    flushTimer = setInterval(() => flushMetrics(), config.flushInterval);
}

/**
 * 버퍼의 메트릭을 서버로 전송
 * @param {boolean} sync - 동기 전송 여부 (beforeunload 시)
 */
async function flushMetrics(sync = false) {
    if (metricsBuffer.length === 0 || !config.endpoint) return;

    const metricsToSend = [...metricsBuffer];
    metricsBuffer = [];

    const payload = {
        metrics: metricsToSend,
        meta: {
            appName: config.appName,
            appVersion: config.appVersion,
            sessionId,
            timestamp: Date.now(),
            userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
            url: typeof window !== 'undefined' ? window.location.href : ''
        }
    };

    debugLog('Flushing metrics:', metricsToSend.length);

    try {
        if (sync && navigator.sendBeacon) {
            // 페이지 언로드 시 sendBeacon 사용 (비차단)
            navigator.sendBeacon(config.endpoint, JSON.stringify(payload));
        } else {
            // originalFetch 사용하여 자동 추적 방지
            const fetchFn = originalFetch || window.fetch;
            const response = await fetchFn(config.endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload),
                keepalive: true  // 페이지 전환 시에도 요청 유지
            });

            // 404 등 서버 에러 시 조용히 처리 (QPM 서버가 없을 수 있음)
            if (!response.ok) {
                debugLog(`Flush response: ${response.status} ${response.statusText}`);
            }
        }
    } catch (error) {
        // 전송 실패 시 조용히 처리 (콘솔에 에러 표시하지 않음)
        // 버퍼에 다시 추가하지 않음 - 서버가 없으면 계속 실패할 것임
        debugLog('Flush failed:', error.message);
    }
}

// ============================================
// 유틸리티 함수
// ============================================

function generateSessionId() {
    return 'rum_' + Date.now().toString(36) + '_' + Math.random().toString(36).substr(2, 9);
}

function shouldSample() {
    return Math.random() < config.sampleRate;
}

function normalizeUrl(url) {
    try {
        const parsed = new URL(url, window.location.origin);
        // 쿼리 파라미터에서 민감한 정보 제거
        const params = new URLSearchParams(parsed.search);
        ['token', 'key', 'password', 'secret', 'auth', 'apikey', 'api_key', 'access_token'].forEach(key => {
            if (params.has(key)) params.set(key, '[REDACTED]');
        });
        return parsed.pathname + (params.toString() ? '?' + params.toString() : '');
    } catch {
        return url;
    }
}

function getFirstPaint() {
    const entries = performance.getEntriesByType?.('paint') || [];
    const fp = entries.find(e => e.name === 'first-paint');
    return fp ? Math.round(fp.startTime) : 0;
}

function getFirstContentfulPaint() {
    const entries = performance.getEntriesByType?.('paint') || [];
    const fcp = entries.find(e => e.name === 'first-contentful-paint');
    return fcp ? Math.round(fcp.startTime) : 0;
}

function debugLog(...args) {
    if (config.debug) {
        console.log('[RUM]', ...args);
    }
}

/**
 * RUM SDK 인스턴스 반환
 */
export function getRUM() {
    return {
        config,
        sessionId,
        qpm: qpmInstance,
        bufferSize: metricsBuffer.length
    };
}

/**
 * RUM SDK 종료
 */
export function destroyRUM() {
    flushMetrics(true);
    disableAutoTracking();

    if (flushTimer) {
        clearInterval(flushTimer);
        flushTimer = null;
    }

    if (qpmInstance) {
        qpmInstance.destroy();
        qpmInstance = null;
    }

    sessionId = null;
    metricsBuffer = [];
    clsValue = 0;

    debugLog('Destroyed');
}

/**
 * RUM/QPM 트레이스 수집 활성화/비활성화 토글
 * @param {boolean} enabled - true: 활성화, false: 비활성화
 */
export function setRUMEnabled(enabled) {
    // 전체 스위치가 꺼져 있으면 켜 달라는 요청을 무시한다.
    // 껐다는 말이 화면의 토글로 뒤집히면 껐다고 할 수 없다.
    if (!QPM_ENABLED) {
        config.enabled = false;
        debugLog('QPM is disabled — enable request ignored');
        return false;
    }

    config.enabled = enabled;

    if (enabled) {
        // 활성화 시 자동 추적 시작
        if (!originalFetch) {
            enableAutoTracking();
        }
        if (!flushTimer) {
            startPeriodicFlush();
        }
        // QPM 인스턴스 재초기화 (이전에 destroy된 경우)
        if (!qpmInstance && config.endpoint) {
            try {
                const { createQPM } = require('../lib/qpm-sdk/frontend');
                const qpmEndpoint = config.endpoint.replace('/api/client-metrics', '');
                if (qpmEndpoint && !qpmEndpoint.endsWith('/api/client-metrics')) {
                    qpmInstance = createQPM({
                        serviceName: config.appName,
                        qpmServerUrl: qpmEndpoint,
                        sampleRate: config.sampleRate,
                        batchSize: config.maxBatchSize,
                        flushInterval: config.flushInterval,
                        slowThreshold: config.slowThreshold
                    });
                    qpmInstance.init().catch(err => {
                        debugLog('QPM SDK re-init warning:', err.message);
                    });
                }
            } catch (error) {
                debugLog('QPM SDK re-init skipped:', error.message);
            }
        }
        debugLog('RUM enabled');
    } else {
        // 비활성화 시 자동 추적 중지 및 버퍼 비우기
        disableAutoTracking();
        if (flushTimer) {
            clearInterval(flushTimer);
            flushTimer = null;
        }
        metricsBuffer = [];
        // QPM 인스턴스 중지 (TimeSync 포함)
        if (qpmInstance) {
            try {
                qpmInstance.destroy();
                qpmInstance = null;
                debugLog('QPM instance destroyed (TimeSync stopped)');
            } catch (error) {
                debugLog('QPM destroy error:', error.message);
            }
        }
        debugLog('RUM disabled');
    }

    return config.enabled;
}

/**
 * RUM 활성화 상태 확인
 * @returns {boolean} 활성화 여부
 */
export function isRUMEnabled() {
    return QPM_ENABLED && config.enabled;
}

// ============================================
// 내보내기
// ============================================

export default {
    initRUM,
    trackAPICall,
    trackEvent,
    trackError,
    trackTiming,
    createTrackedFetch,
    enableAutoTracking,
    disableAutoTracking,
    useRenderTracking,
    getRUM,
    destroyRUM,
    setRUMEnabled,
    isRUMEnabled
};
